import { ActionIcon, Affix, Transition } from '@mantine/core';
import { IconArrowDown } from '@tabler/icons-react';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMedia } from '@mygpt/app/utils';

function useScrollToBottom() {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  function scrollDomToBottom() {
    const dom = scrollRef.current;
    if (dom) {
      requestAnimationFrame(() => {
        setAutoScroll(true);
        dom.scrollTo(0, dom.scrollHeight);
      });
    }
  }

  return {
    scrollRef,
    autoScroll,
    setAutoScroll,
    scrollDomToBottom,
  };
}

const scrollableDivStyle = {
  maxHeight: 'inherit',
  height: 'inherit',
  overflowY: 'auto' as const,
};

export type ChatScrollProps = {
  className?: string;
  onScroll?: (isAtBottom: boolean) => void;
  children?: React.ReactNode;
  closeAffixArrow?: boolean;
  affixPosition?: {
    bottom?: string | number;
    left?: string | number;
  };
  style?: React.CSSProperties;
  containerScroll?: (e: HTMLElement) => void;
  loading?: boolean; // 是否在加载状态（消息已发送但还没开始返回）
  pending?: boolean; // 是否正在输出 AI 回复
};

export const ChatScroll: React.FC<ChatScrollProps> = ({
  affixPosition,
  children,
  className,
  closeAffixArrow,
  style,
  containerScroll,
  loading,
  onScroll,
  pending,
}) => {
  const { autoScroll, scrollDomToBottom, scrollRef, setAutoScroll } = useScrollToBottom();
  const bottomRef = useRef<HTMLDivElement>(null);
  const questionCountRef = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastQuestionRef = useRef<HTMLElement | null>(null);
  const spacerMarkerRef = useRef<HTMLDivElement>(null); // 标记占位前的位置
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0); // 强制触发占位计算

  const { isMobile } = useMedia();
  const joinedClassName = className ? ' ' + className : '';

  const onChatBodyScroll = (e: HTMLElement) => {
    const bottomHeight = e.scrollTop + e.clientHeight;
    const isHitBottom = bottomHeight >= e.scrollHeight - (isMobile ? 4 : 10);
    setAutoScroll(isHitBottom);
    onScroll?.(isHitBottom);
  };

  // 滚动到用户消息（带防抖）
  const scrollToQuestion = useCallback((questionElement: HTMLElement) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const container = scrollRef.current;
      if (!container) return;

      const containerTop = container.scrollTop;
      const questionTop = questionElement.offsetTop;
      const offset = 24; // 距离顶部的间距

      container.scrollTo({
        top: questionTop - offset,
        behavior: 'smooth',
      });
    }, 100); // 100ms 防抖
  }, []);

  // 监听新用户消息并滚动到顶部
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      // 查找所有用户消息（通过 className 包含 "questionText"）
      const questionElements = container.querySelectorAll('[class*="questionText"]');
      const currentCount = questionElements.length;

      if (currentCount > questionCountRef.current) {
        questionCountRef.current = currentCount;
        const lastQuestion = questionElements[questionElements.length - 1] as HTMLElement;
        if (lastQuestion) {
          lastQuestionRef.current = lastQuestion;
          scrollToQuestion(lastQuestion);
          // 触发占位计算
          setForceUpdate((prev) => prev + 1);
        }
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [scrollToQuestion]);

  // 动态计算占位 DOM 高度 - 实时跟随内容变化
  useEffect(() => {
    const container = scrollRef.current;
    const spacerMarker = spacerMarkerRef.current;

    if (!container || !spacerMarker) {
      return;
    }

    const calculateSpacerHeight = () => {
      // 查找最后一个用户消息
      const questionElements = container.querySelectorAll('[class*="questionText"]');
      if (questionElements.length === 0) {
        setSpacerHeight(0);
        return;
      }

      const lastQuestion = questionElements[questionElements.length - 1] as HTMLElement;
      if (!lastQuestion) {
        setSpacerHeight(0);
        return;
      }

      const containerHeight = container.clientHeight;

      // 计算从最后用户消息到标记元素的内容高度
      const questionRect = lastQuestion.getBoundingClientRect();
      const markerRect = spacerMarker.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // 内容高度 = 从用户消息开始到标记元素的高度
      const contentHeight = markerRect.top - questionRect.top;

      // 占位高度 = 容器高度 - 内容高度
      // 只有当内容高度 < 容器高度时才需要占位
      // 无论是否在 loading/pending 状态，只要内容没占满，就保持占位
      const neededHeight = containerHeight - contentHeight;

      setSpacerHeight(Math.max(0, neededHeight));
    };

    // 初始计算
    calculateSpacerHeight();

    // 监听容器的尺寸变化
    const resizeObserver = new ResizeObserver(() => {
      calculateSpacerHeight();
    });

    resizeObserver.observe(container);

    // 使用 MutationObserver 监听内容变化（答案输出）
    const mutationObserver = new MutationObserver(() => {
      calculateSpacerHeight();
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [loading, pending, forceUpdate]);

  // 清除会话时重置状态
  useEffect(() => {
    const container = scrollRef.current;
    const resizeObserver = new ResizeObserver(() => {
      if (container) {
        const scrollable = container.scrollHeight > container.clientHeight;
        if (!scrollable) {
          setAutoScroll(true);
          setSpacerHeight(0);
        }
      }
    });

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={joinedClassName}
      style={style || scrollableDivStyle}
      ref={scrollRef}
      onScroll={(e) => {
        onChatBodyScroll(e.currentTarget);
        containerScroll?.(e.currentTarget);
      }}
    >
      {children}
      {/* 标记元素，用于计算占位前的内容高度 */}
      <div ref={spacerMarkerRef} style={{ height: 0, overflow: 'hidden' }} />
      {/* 动态占位 DOM，实时跟随内容变化 */}
      {spacerHeight > 0 && <div style={{ height: `${spacerHeight}px` }} />}
      {!closeAffixArrow && (
        <Affix position={{ bottom: affixPosition?.bottom || 100, left: affixPosition?.left || '50%' }}>
          <Transition transition="slide-up" mounted={!autoScroll}>
            {(transitionStyles) => (
              <ActionIcon
                style={{ ...transitionStyles, backgroundColor: 'rgba(0,0,0,.1)' }}
                radius="xl"
                variant="light"
                onClick={scrollDomToBottom}
              >
                <IconArrowDown color="rgba(0,0,0,.5)" size={16} />
              </ActionIcon>
            )}
          </Transition>
        </Affix>
      )}
      <div ref={bottomRef} />
    </div>
  );
};
